import { useEffect, useMemo, useState } from 'react';
import {
  CartesianGrid, Legend, Line, LineChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import api from '../../config/axiosConfig';
import useGrandeEcole from '../../stores/useGrandeEcole';

export function StatsGlobales({ darkMode = true }) {
  const {
    totalLearners,
    activeLearners,
    totalPesasCirculating,
    globalCompletionRate,
    learners,
    courses,
    loading,
  } = useGrandeEcole();

  // ── Séries temporelles depuis l'API ──────────────────────────────────
  const [series,      setSeries]      = useState([]);
  const [seriesError, setSeriesError] = useState(null);
  const [seriesLoad,  setSeriesLoad]  = useState(false);

  useEffect(() => {
    const fetchSeries = async () => {
      setSeriesLoad(true);
      try {
        const res = await api.get('/admin/stats/timeseries?days=7');
        setSeries(res.data.series || []);
      } catch (err) {
        setSeriesError(err.message);
        // Fallback : construire une série minimale depuis les données du store
        // Un seul point "maintenant" — mieux que des données fictives
        setSeries([{
          month:      'Maintenant',
          completion: globalCompletionRate || 0,
          active:     activeLearners       || 0,
          pesas:      Math.round((totalPesasCirculating || 0) / 1000),
        }]);
      } finally {
        setSeriesLoad(false);
      }
    };
    fetchSeries();
  }, [globalCompletionRate, activeLearners, totalPesasCirculating]);

  // ── Métriques calculées ───────────────────────────────────────────────
  const activeRate = totalLearners
    ? Math.round((activeLearners / totalLearners) * 100)
    : 0;

  const learnersInProgress = useMemo(() =>
    learners.filter(l => l.status === 'active' && (l.globalProgress || 0) > 0).length,
  [learners]);

  const avgProgress = useMemo(() => {
    const active = learners.filter(l => l.globalProgress != null);
    if (!active.length) return 0;
    return Math.round(active.reduce((s, l) => s + l.globalProgress, 0) / active.length);
  }, [learners]);

  if (loading) {
    return (
      <div className="h-80 bg-gradient-to-br from-zinc-900 to-black rounded-xl animate-pulse" />
    );
  }

  return (
    <div className="space-y-10 font-mono">

      {/* Header */}
      <div className="border-b border-zinc-800 pb-5">
        <h3 className="text-2xl font-black tracking-tighter text-cyan-300 uppercase">
          STATISTIQUES ET VECTEURS D'IMPACT
        </h3>
        <p className="mt-1 text-xs uppercase tracking-widest text-zinc-600">
          Real-time snapshot —{' '}
          {new Date().toLocaleDateString('fr-FR', {
            year: 'numeric', month: 'short', day: 'numeric',
          })}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

        {/* ── Core Metrics ─────────────────────────────────────────────── */}
        <div className="border border-zinc-800 bg-zinc-950/80 p-6 rounded-md">
          <h4 className="text-lg font-black text-cyan-400 mb-6 uppercase tracking-wider">
            Core Metrics
          </h4>

          <div className="space-y-7">

            {/* Completion rate */}
            <div className="space-y-2">
              <div className="flex justify-between items-baseline text-sm text-zinc-400 uppercase">
                Completion Rate
                <span className="text-4xl font-black text-cyan-300">
                  {globalCompletionRate || 0}%
                </span>
              </div>
              <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-cyan-500 transition-all duration-700"
                  style={{ width: `${globalCompletionRate || 0}%` }}
                />
              </div>
            </div>

            {/* Pesas */}
            <div className="flex justify-between items-baseline text-sm text-zinc-400 uppercase">
              Circulating Pesas
              <span className="text-4xl font-black text-orange-400">
                {(totalPesasCirculating || 0).toLocaleString('fr-FR')} ₧
              </span>
            </div>

            {/* Active learners */}
            <div className="space-y-2">
              <div className="flex justify-between items-baseline text-sm text-zinc-400 uppercase">
                Active Learners
                <span className="text-4xl font-black text-emerald-400">
                  {activeLearners || 0}{' '}
                  <span className="text-xl text-zinc-600">/ {totalLearners || 0}</span>
                </span>
              </div>
              <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500 transition-all duration-700"
                  style={{ width: `${activeRate}%` }}
                />
              </div>
            </div>

            {/* Stats secondaires depuis le store */}
            <div className="grid grid-cols-3 gap-3 pt-4 border-t border-zinc-800">
              {[
                { label: 'En progression', value: learnersInProgress,  color: 'text-sky-400'    },
                { label: 'Moy. progression', value: `${avgProgress}%`, color: 'text-violet-400' },
                { label: 'Parcours actifs',  value: courses.length,    color: 'text-orange-400' },
              ].map(({ label, value, color }) => (
                <div key={label} className="text-center">
                  <div className={`text-xl font-black ${color}`}>{value}</div>
                  <div className="text-[9px] text-zinc-600 uppercase tracking-widest mt-0.5">
                    {label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Impact Vectors — graphe ───────────────────────────────────── */}
        <div className="border border-zinc-800 bg-zinc-950/80 p-6 rounded-md flex flex-col">
          <div className="flex items-center justify-between mb-6">
            <h4 className="text-lg font-black text-cyan-400 uppercase tracking-wider">
              Impact Vectors
            </h4>
            {seriesError && (
              <span className="text-[9px] text-amber-500 font-mono uppercase tracking-wider">
                API indisponible — snapshot local
              </span>
            )}
            {seriesLoad && (
              <span className="text-[9px] text-zinc-600 font-mono uppercase animate-pulse">
                Chargement...
              </span>
            )}
          </div>

          <div className="flex-1 min-h-[300px]">
            {series.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={series}
                  margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                  <XAxis
                    dataKey="month"
                    stroke="#6b7280"
                    fontSize={11}
                    tickFormatter={v => v?.slice(5) || v} // affiche MM-DD si format ISO
                  />
                  <YAxis stroke="#6b7280" fontSize={11} />
                  <Tooltip
                    contentStyle={{
                      background: '#0f172a',
                      border: '1px solid #1e293b',
                      color: '#e2e8f0',
                    }}
                    labelStyle={{ color: '#94a3b8' }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12, color: '#94a3b8' }} />
                  <Line
                    type="monotone"
                    dataKey="completion"
                    name="Completion %"
                    stroke="#22d3ee"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 6 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="active"
                    name="Active Users"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 6 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="pesas"
                    name="Pesas (k)"
                    stroke="#f97316"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-zinc-700 text-xs uppercase tracking-widest">
                Données insuffisantes pour tracer les vecteurs
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}